import Box from '@mui/material/Box';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { useCallback } from 'react';
import type { AudienceListType } from '../../../../../../../../modules/audiences/AudienceForm/types';
import { audienceLocation, type SelectedLocationSegment } from '../../../constants';
import { useLocationOptions } from '../../../hooks/useLocationOptions';
import { LocationCongressional } from './LocationCongressional';
import { LocationDma } from './LocationDma';
import type { SegmentSetters, Segments } from './LocationSection';
import { LocationState } from './LocationState';
import { LocationZipCode } from './LocationZipCode';

export type LocationContentProps = {
  isEdit: boolean;
  listType: AudienceListType;
  selectedLocationSegment: SelectedLocationSegment;
  segmentSetters: SegmentSetters;
  segments: Segments;
};

export const LocationContent = ({
  isEdit,
  listType,
  selectedLocationSegment,
  segments,
  segmentSetters,
}: LocationContentProps) => {
  const { data: locationData, loading: locationLoading, error: locationError } = useLocationOptions();

  const renderLocationContent = useCallback(() => {
    switch (selectedLocationSegment) {
      case audienceLocation.State:
        return (
          <LocationState
            isEdit={isEdit}
            listType={listType}
            locationStateSegments={segments.state}
            setLocationStateSegments={segmentSetters.state}
            options={locationData?.stateSegments || []}
          />
        );

      case audienceLocation.DMA:
        return (
          <LocationDma
            isEdit={isEdit}
            listType={listType}
            locationDmaSegments={segments.dma}
            setLocationDmaSegments={segmentSetters.dma}
            options={locationData?.dmaSegments || []}
          />
        );

      case audienceLocation.ZipCode:
        return (
          <LocationZipCode
            isEdit={isEdit}
            listType={listType}
            locationZipCodeSegments={segments.zip}
            setLocationZipCodeSegments={segmentSetters.zip}
          />
        );

      case audienceLocation.CongressionalDistrict:
        return (
          <LocationCongressional
            isEdit={isEdit}
            listType={listType}
            locationCongressionalSegments={segments.congressional}
            setLocationCongressionalSegments={segmentSetters.congressional}
            options={locationData?.congressionalSegments || []}
          />
        );
      default:
        return null;
    }
  }, [isEdit, listType, selectedLocationSegment, segments, segmentSetters, locationData]);

  const isZipCode = selectedLocationSegment === audienceLocation.ZipCode;
  const showLoader = locationLoading && !isZipCode;
  const showError = locationError && !isZipCode;

  if (showLoader) return <Loader />;
  if (showError) throw new Error('Query Error');

  return (
    <Box
      sx={{
        pb: 2,
      }}
    >
      {renderLocationContent()}
    </Box>
  );
};
